import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import routeAdmin from "./routes";
import { useDispatch, useSelector } from "react-redux";
import { routeAuth } from "../AuthPage";
import { Redirect } from "react-router-dom";
import { deleteRequest, getRequest, putRequest } from "../../services/requestController";
import { setLoading, setProfile } from "../../redux/actions";
import dayjs from "dayjs";
import { getActionLabel } from "../../utils/actionLabels";

import {
    Alert,
    Button,
    Card,
    Col,
    Collapse,
    Descriptions,
    Divider,
    Drawer,
    Empty,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Progress,
    Row,
    Segmented,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
    notification,
} from "antd";

import {
    BarChartOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    DatabaseOutlined,
    DollarOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,
    FundOutlined,
    MinusOutlined,
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined,
    TeamOutlined,
    WalletOutlined,
} from "@ant-design/icons";

import { UserHeader } from "../../components/UserHeader/UserHeader";
import EditModal from "../../components/Modals/EditModal/EditModal";

import "./styles.scss";
import HideForManager from "../../components/RoleGate/HideForManager";

const { Title, Text } = Typography;

const STATUS_OPTIONS = [
    { label: "Активный", value: 1 },
    { label: "Заблокированный", value: 2 },
];

const isEmptyValue = (v) => {
    if (v === null || v === undefined) return true;
    if (typeof v === "string") return v.trim() === "";
    return false;
};

const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const toShort = (v, max = 36) => {
    const s = String(v ?? "");
    if (s.length <= max) return s;
    return `${s.slice(0, max).trim()}…`;
};

const toOnlineMeta = (rawOnline) => {
    if (!rawOnline) return { text: "—", days: null, hours: null, ts: null };
    const d = new Date(rawOnline);
    if (Number.isNaN(d.getTime())) return { text: "—", days: null, hours: null, ts: null };

    const now = new Date();
    const diffMs = Math.max(0, now.getTime() - d.getTime());
    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    let text;
    if (minutes < 1) {
        text = "Только что";
    } else if (minutes < 60) {
        text = `${minutes} мин назад`;
    } else if (hours < 24) {
        text = `${hours} ч назад`;
    } else if (days === 1) {
        text = "1 день назад";
    } else if (days < 5) {
        text = `${days} дня назад`;
    } else {
        text = `${days} дней назад`;
    }

    return { text, days, hours, ts: d.getTime() };
};

const AdminPage = () => {
    const dispatch = useDispatch();
    const loading = useSelector((state) => state.loading);
    const token = useSelector((state) => state.token);
    const profileData = useSelector((state) => state.profileData);
    const isSuperAdmin = Number(profileData?.role) === 1;
    const canManageBru = isSuperAdmin || !!profileData?.permissions?.canManageBru;

    const [usersData, setUsersData] = useState([]);
    const [usersById, setUsersById] = useState({});

    const [balanceModalOpen, setBalanceModalOpen] = useState(false);
    const [balanceConfirmLoading, setBalanceConfirmLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [balanceModalKey, setBalanceModalKey] = useState(0);
    const [balanceInputs, setBalanceInputs] = useState({ pyataki: "", balance: "" });

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editConfirmLoading, setEditConfirmLoading] = useState(false);
    const [editSelected, setEditSelected] = useState(null);

    const [tableData, setTableData] = useState([]);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    const [statusFilter, setStatusFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [insightsOpen, setInsightsOpen] = useState(false);

    const [logsModalOpen, setLogsModalOpen] = useState(false);
    const [logsModalLoading, setLogsModalLoading] = useState(false);
    const [logsModalUser, setLogsModalUser] = useState(null);
    const [userLogs, setUserLogs] = useState([]);
    const [selectedLog, setSelectedLog] = useState(null);
    const [logDetailOpen, setLogDetailOpen] = useState(false);

    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [historyModalLoading, setHistoryModalLoading] = useState(false);
    const [historyModalUser, setHistoryModalUser] = useState(null);
    const [userHistory, setUserHistory] = useState([]);

    const [referralModalOpen, setReferralModalOpen] = useState(false);
    const [referralModalLoading, setReferralModalLoading] = useState(false);
    const [referralModalUser, setReferralModalUser] = useState(null);
    const [referralData, setReferralData] = useState(null);

    const [showAllBalances, setShowAllBalances] = useState(false);
    const [visibleBalances, setVisibleBalances] = useState({});

    // Refs to avoid stale closures inside memoized columns without putting full arrays in deps
    const tableDataRef = useRef(tableData);
    const usersByIdRef = useRef(usersById);
    const tokenRef = useRef(token);
    const showAllBalancesRef = useRef(showAllBalances);
    const visibleBalancesRef = useRef(visibleBalances);

    useEffect(() => { tableDataRef.current = tableData; }, [tableData]);
    useEffect(() => { usersByIdRef.current = usersById; }, [usersById]);
    useEffect(() => { tokenRef.current = token; }, [token]);
    useEffect(() => { showAllBalancesRef.current = showAllBalances; }, [showAllBalances]);
    useEffect(() => { visibleBalancesRef.current = visibleBalances; }, [visibleBalances]);

    const toggleBalanceVisibility = useCallback((userId) => {
        setVisibleBalances(prev => {
            const next = { ...prev, [userId]: !prev[userId] };
            visibleBalancesRef.current = next;
            return next;
        });
    }, []);

    const isBalanceVisible = useCallback((userId) => {
        return showAllBalancesRef.current || visibleBalancesRef.current[userId];
    }, []);

    const handleDeleteUser = useCallback(async (userId) => {
        const userIdNum = Number(userId);
        const deleteUserResponse = await deleteRequest(`/api/admin/telegram/user/${userIdNum}`, tokenRef.current);

        if (deleteUserResponse.success) {
            setTableData((prevData) => {
                const next = prevData.filter((user) => user.userId !== userIdNum);
                setPagination((p) => {
                    const maxPage = Math.max(1, Math.ceil(next.length / p.pageSize));
                    return p.current > maxPage ? { ...p, current: maxPage } : p;
                });
                return next;
            });

            setUsersData((prevData) => ({
                ...prevData,
                count: prevData.count - 1,
                data: prevData.data ? prevData.data.filter((user) => user.userId !== userIdNum) : [],
            }));

            setUsersById((prev) => {
                const next = { ...prev };
                delete next[userIdNum];
                return next;
            });

            notification.success({
                message: deleteUserResponse?.data?.message,
                description: `Пользователь с User ID: ${deleteUserResponse?.data?.userId} успешно удален`,
            });
        } else {
            notification.error({
                message: deleteUserResponse.error.message || "Ошибка",
                description: (deleteUserResponse.error.description || []).join("\n"),
            });
        }
    }, []);

    const openLogsModal = useCallback(async (userId) => {
        const uId = Number(userId);
        const u = tableDataRef.current.find((x) => Number(x.userId) === uId);

        if (!u) {
            notification.error({ message: "Ошибка", description: "Пользователь не найден в таблице" });
            return;
        }

        setLogsModalUser({ userId: uId, name: u.name });
        setLogsModalOpen(true);
        setLogsModalLoading(true);

        try {
            const res = await getRequest(`/api/admin/logs/telegram/${uId}`, tokenRef.current);

            if (!res?.success) {
                notification.error({
                    message: res?.error?.message || "Ошибка",
                    description: (res?.error?.description || []).join("\n"),
                });
                setUserLogs([]);
                return;
            }

            const logs = Array.isArray(res.data?.data) ? res.data.data : [];
            setUserLogs(logs);
        } catch {
            notification.error({ message: "Ошибка", description: "Не удалось загрузить логи" });
            setUserLogs([]);
        } finally {
            setLogsModalLoading(false);
        }
    }, []);

    const closeLogsModal = useCallback(() => {
        setLogsModalOpen(false);
        setLogsModalUser(null);
        setUserLogs([]);
    }, []);

    const openHistoryModal = useCallback(async (userId) => {
        const uId = Number(userId);
        const u = tableDataRef.current.find((x) => Number(x.userId) === uId);

        if (!u) {
            notification.error({ message: "Ошибка", description: "Пользователь не найден" });
            return;
        }

        setHistoryModalUser({ userId: uId, name: u.name });
        setHistoryModalOpen(true);
        setHistoryModalLoading(true);

        try {
            const res = await getRequest(`/api/history/${uId}?limit=50`, tokenRef.current);

            if (!res?.success) {
                notification.error({ message: "Ошибка", description: res?.error?.message || "Не удалось загрузити историю" });
                setUserHistory([]);
                return;
            }

            const history = Array.isArray(res.data?.data?.history) ? res.data.data.history : [];
            setUserHistory(history);
        } catch {
            notification.error({ message: "Ошибка", description: "Не удалось загрузить историю" });
            setUserHistory([]);
        } finally {
            setHistoryModalLoading(false);
        }
    }, []);

    const closeHistoryModal = useCallback(() => {
        setHistoryModalOpen(false);
        setHistoryModalUser(null);
        setUserHistory([]);
    }, []);

    const openReferralModal = useCallback(async (userId) => {
        const uId = Number(userId);
        const u = tableDataRef.current.find((x) => Number(x.userId) === uId);

        if (!u) {
            notification.error({ message: "Ошибка", description: "Пользователь не найден" });
            return;
        }

        setReferralModalUser({ userId: uId, name: u.name });
        setReferralModalOpen(true);
        setReferralModalLoading(true);

        try {
            const res = await getRequest(`/api/referral/stats/${uId}`, tokenRef.current);

            if (!res?.success) {
                notification.error({ message: "Ошибка", description: res?.error?.message || "Не удалось загрузить данные" });
                setReferralData(null);
                return;
            }

            setReferralData(res.data?.data || null);
        } catch {
            notification.error({ message: "Ошибка", description: "Не удалось загрузить реферальные данные" });
            setReferralData(null);
        } finally {
            setReferralModalLoading(false);
        }
    }, []);

    const closeReferralModal = useCallback(() => {
        setReferralModalOpen(false);
        setReferralModalUser(null);
        setReferralData(null);
    }, []);

    const openBalanceModal = useCallback(async (userId) => {
        const uId = Number(userId);
        const u = tableDataRef.current.find((x) => Number(x.userId) === uId);

        if (!u) {
            notification.error({ message: "Ошибка", description: "Пользователь не найден в таблице" });
            return;
        }

        try {
            const res = await getRequest(`/api/admin/telegram/user/${uId}/balance`, tokenRef.current);
            if (res?.success) {
                const payload = res?.data?.data ?? res?.data ?? {};
                setSelectedUser({
                    userId: uId,
                    name: payload.name || u.name,
                    balance: Number(payload.balance) || 0,
                    pyataki: Number(payload.pyataki) || 0,
                    gamesPlayed: Number(payload.gamesPlayed) || 0,
                });
            } else {
                setSelectedUser({
                    userId: uId,
                    name: u.name,
                    balance: Number(u.balance) || 0,
                    pyataki: Number(u.pyataki) || 0,
                    gamesPlayed: 0,
                });
            }
        } catch {
            setSelectedUser({
                userId: uId,
                name: u.name,
                balance: Number(u.balance) || 0,
                pyataki: Number(u.pyataki) || 0,
                gamesPlayed: 0,
            });
        }
        setBalanceModalKey(k => k + 1);
        setBalanceModalOpen(true);
    }, []);

    const closeBalanceModal = useCallback(() => {
        setBalanceModalOpen(false);
        setSelectedUser(null);
        setBalanceConfirmLoading(false);
        setBalanceInputs({ pyataki: "", balance: "" });
    }, []);

    const submitBalanceAction = async (currency, action) => {
        if (!selectedUser?.userId) {
            notification.error({ message: "Ошибка", description: "Не выбран пользователь" });
            return;
        }

        const inputVal = Number(balanceInputs[currency]);
        if (!Number.isFinite(inputVal) || inputVal <= 0) {
            notification.error({ message: "Ошибка", description: "Введите положительное число" });
            return;
        }

        const currentPyataki = Number(selectedUser.pyataki) || 0;
        const currentBru = Number(selectedUser.balance) || 0;

        let newPyataki = currentPyataki;
        let newBru = currentBru;

        if (currency === "pyataki") {
            newPyataki = action === "add" ? currentPyataki + inputVal : Math.max(0, currentPyataki - inputVal);
        } else {
            newBru = action === "add"
                ? Number((currentBru + inputVal).toFixed(2))
                : Number(Math.max(0, currentBru - inputVal).toFixed(2));
        }

        setBalanceConfirmLoading(true);
        try {
            const body = {
                userId: Number(selectedUser.userId),
                ...(currency === "pyataki" ? { pyataki: newPyataki } : { balance: newBru }),
            };
            const res = await putRequest("/api/admin/telegram/user/balance", body, token);

            if (!res?.success) {
                notification.error({
                    message: res?.error?.message || "Ошибка",
                    description: (res?.error?.description || []).join("\n"),
                });
                return;
            }
            if (res?.data?.success === false) {
                notification.error({ message: "Ошибка", description: res?.data?.message || "Ошибка" });
                return;
            }

            const label = currency === "pyataki" ? "пятаков" : "BRU";
            const sign = action === "add" ? "+" : "−";
            notification.success({ message: "Успешно", description: `${sign}${inputVal} ${label}` });

            setSelectedUser((prev) => prev ? { ...prev, pyataki: newPyataki, balance: newBru } : prev);
            setBalanceInputs((prev) => ({ ...prev, [currency]: "" }));
            setTableData((prev) =>
                prev.map((user) => {
                    if (Number(user.userId) !== Number(selectedUser.userId)) return user;
                    return { ...user, balance: newBru, pyataki: newPyataki };
                })
            );
        } finally {
            setBalanceConfirmLoading(false);
        }
    };

    const buildEditFields = (rawUser) => {
        const excluded = new Set(["balance", "createdAt", "updatedAt"]);
        const entries = Object.entries(rawUser || {}).filter(([k, v]) => {
            if (excluded.has(k)) return false;
            if (v === undefined) return false;
            if (Array.isArray(v)) return false;
            if (v && typeof v === "object") return false;
            return true;
        });

        const fields = entries.map(([k, v]) => {
            if (k === "userId") {
                return {
                    type: "number",
                    name: "userId",
                    label: "User ID",
                    disabled: true,
                    rules: [{ required: true, message: "User ID обязателен" }],
                    props: { style: { width: "100%" } },
                };
            }

            if (k === "user") {
                return {
                    type: "input",
                    name: "user",
                    label: "Пользователь",
                    disabled: true,
                    rules: [{ required: true, message: "Пользователь обязателен" }],
                    props: { style: { width: "100%" } },
                };
            }

            if (k === "online") {
                return {
                    type: "input",
                    name: "online",
                    label: "Online",
                    disabled: true,
                    props: { style: { width: "100%" } },
                };
            }

            if (k === "status") {
                return {
                    type: "select",
                    name: "status",
                    label: "Статус",
                    options: STATUS_OPTIONS,
                    rules: [{ required: true, message: "Выберите статус" }],
                    props: { style: { width: "100%" } },
                };
            }

            if (typeof v === "number") {
                return {
                    type: "number",
                    name: k,
                    label: k,
                    props: { style: { width: "100%" } },
                };
            }

            return {
                type: "input",
                name: k,
                label: k,
                props: { style: { width: "100%" } },
            };
        });

        const order = ["userId", "user"];
        return [...order.map((n) => fields.find((f) => f.name === n)).filter(Boolean), ...fields.filter((f) => !order.includes(f.name))];
    };

    const openEditModal = useCallback((userId) => {
        const uId = Number(userId);
        const raw = usersByIdRef.current?.[uId];

        if (!raw) {
            notification.error({ message: "Ошибка", description: "Не удалось загрузить данные пользователя для редактирования" });
            return;
        }

        setEditSelected({
            userId: uId,
            displayName: raw?.name || String(uId),
            rawUser: raw,
        });

        setEditModalOpen(true);
    }, []);

    const closeEditModal = useCallback(() => {
        setEditModalOpen(false);
        setEditSelected(null);
        setEditConfirmLoading(false);
    }, []);

    const submitEditUser = async (values) => {
        const raw = editSelected?.rawUser;

        if (!raw) {
            notification.error({ message: "Ошибка", description: "Не выбран пользователь" });
            return;
        }

        const baseBody = {
            ...values,
            userId: values?.userId ?? raw.userId,
            status: values?.status ?? raw.status,
        };

        delete baseBody.balance;
        delete baseBody.online;

        const body = Object.keys(baseBody).reduce((acc, key) => {
            const v = baseBody[key];

            if (key === "userId") {
                const id = Number(v);
                if (Number.isFinite(id)) acc[key] = id;
                return acc;
            }

            if (key === "status") {
                const s = Number(v);
                if (Number.isFinite(s)) acc[key] = s;
                return acc;
            }

            if (key === "user") {
                if (!isEmptyValue(v)) acc[key] = String(v).trim();
                return acc;
            }

            if (isEmptyValue(v)) return acc;

            if (typeof v === "string") {
                acc[key] = v.trim();
                return acc;
            }

            acc[key] = v;
            return acc;
        }, {});

        if (!Number.isFinite(Number(body.userId))) {
            notification.error({ message: "Ошибка", description: "Нельзя отправить запрос без userId" });
            return;
        }

        if (!Number.isFinite(Number(body.status))) {
            notification.error({ message: "Ошибка", description: "Некорректный статус" });
            return;
        }

        setEditConfirmLoading(true);
        try {
            const res = await putRequest("/api/admin/telegram/user", body, token);

            if (!res?.success) {
                notification.error({
                    message: res?.error?.message || "Ошибка",
                    description: (res?.error?.description || []).join("\n"),
                });
                return;
            }

            if (res?.data?.success === false) {
                notification.error({ message: "Ошибка", description: res?.data?.message || "Ошибка" });
                return;
            }

            notification.success({ message: "Успешно", description: res?.data?.message || "Данные обновлены" });

            const mergedRaw = { ...raw, ...values, userId: Number(body.userId), user: body.user, status: Number(body.status) };

            setUsersById((prev) => ({
                ...prev,
                [Number(editSelected.userId)]: mergedRaw,
            }));

            setTableData((prev) =>
                prev.map((row) => {
                    if (Number(row.userId) !== Number(editSelected.userId)) return row;

                    const statusArray = Number(mergedRaw.status) === 2 ? ["заблокирован"] : ["активный"];

                    return {
                        ...row,
                        name: mergedRaw?.name ?? row.name,
                        status: statusArray,
                        statusValue: Number(mergedRaw.status),
                    };
                })
            );

            closeEditModal();
        } finally {
            setEditConfirmLoading(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                title: "User ID",
                dataIndex: "userId",
                key: "userId",
                width: 110,
                sorter: (a, b) => safeNum(a.userId) - safeNum(b.userId),
            },
            {
                title: "Internal ID",
                dataIndex: "internalId",
                key: "internalId",
                width: 130,
                render: (text) => <Tag color="blue">{text || "—"}</Tag>,
            },
            {
                title: "Имя",
                dataIndex: "name",
                key: "name",
                ellipsis: true,
                render: (text) => <Text strong>{text}</Text>,
            },
            {
                title: "Баланс",
                dataIndex: "balance",
                key: "balance",
                width: 240,
                sorter: (a, b) => safeNum(a.balance) - safeNum(b.balance),
                render: (_, record) => {
                    const visible = isBalanceVisible(record.userId);
                    return (
                        <Space direction="horizontal" size={8} align="center">
                            <Button
                                type="text"
                                size="small"
                                icon={visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                onClick={() => toggleBalanceVisibility(record.userId)}
                                className="balance-toggle-btn"
                                style={{ padding: '4px 8px' }}
                            />
                            {visible ? (
                                <Space direction="vertical" size={2}>
                                    <Text strong style={{ color: '#52c41a' }}>
                                        {safeNum(record.balance).toFixed(2)} BRU
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {safeNum(record.pyataki)} пятаков
                                    </Text>
                                </Space>
                            ) : (
                                <Space direction="vertical" size={2}>
                                    <Text strong className="balance-hidden-text" style={{ color: '#8c8c8c' }}>
                                        ••••••
                                    </Text>
                                    <Text type="secondary" className="balance-hidden-text" style={{ fontSize: 12 }}>
                                        ••••••
                                    </Text>
                                </Space>
                            )}
                        </Space>
                    );
                },
            },
            {
                title: "Последний раз онлайн",
                dataIndex: "online",
                key: "online",
                width: 170,
                render: (v) => <Text type="secondary">{v || "—"}</Text>,
            },
            {
                title: "Статус аккаунта",
                key: "status",
                dataIndex: "status",
                width: 190,
                filters: [
                    { text: "Активный", value: 1 },
                    { text: "Заблокирован", value: 2 },
                ],
                onFilter: (value, record) => safeNum(record.statusValue) === safeNum(value),
                render: (_, { status }) => (
                    <>
                        {status.map((stat) => {
                            let color = "green";
                            if (stat === "заблокирован") color = "volcano";
                            return (
                                <Tag color={color} key={stat}>
                                    {String(stat).toUpperCase()}
                                </Tag>
                            );
                        })}
                    </>
                ),
            },
            {
                title: "Действие",
                key: "action",
                width: 380,
                render: (_, record) => (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, rowGap: 8 }}>
                        <button className={"blue"} onClick={() => openBalanceModal(record.key)}>
                            Баланс
                        </button>
                        <HideForManager>
                            <button className={"blue"} onClick={() => openHistoryModal(record.key)}>
                                История
                            </button>
                            <button className={"blue"} onClick={() => openReferralModal(record.key)}>
                                Рефералы
                            </button>
                            <button className={"blue"} onClick={() => openEditModal(record.key)}>
                                Редактировать
                            </button>
                            <button className={"blue"} onClick={() => openLogsModal(record.key)}>
                                Логи
                            </button>
                            <Popconfirm
                                title="Удаление аккаунта"
                                description="Вы уверены, что хотите удалить этот аккаунт?"
                                icon={<ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />}
                                onConfirm={() => handleDeleteUser(record.key)}
                                okText="Удалить"
                                cancelText="Отмена"
                                okButtonProps={{ danger: true }}
                            >
                                <button className={"red"}>
                                    Удалить
                                </button>
                            </Popconfirm>
                        </HideForManager>
                    </div>
                ),
            },
        ],
        [showAllBalances, visibleBalances, openBalanceModal, openHistoryModal, openReferralModal, openEditModal, openLogsModal, handleDeleteUser, isBalanceVisible, toggleBalanceVisibility]
    );

    useEffect(() => {
        const getProfile = async () => {
            const profileResponse = await getRequest("/api/admin/user", token);
            dispatch(setProfile(profileResponse.data));

            if (!profileResponse.success) {
                notification.error({
                    message: profileResponse.error.message || "Ошибка",
                    description: (profileResponse.error.description || []).join("\n"),
                });
            }
        };

        const getUsers = async () => {
            const usersResponse = await getRequest("/api/admin/telegram/profiles", token);

            setUsersData(usersResponse.data);

            if (usersResponse.success && usersResponse.data?.data) {
                const nextUsersById = {};
                usersResponse.data.data.forEach((u) => {
                    nextUsersById[Number(u.userId)] = u;
                });
                setUsersById(nextUsersById);

                const transformedData = usersResponse.data.data.map((user) => {
                    let statusArray = [];
                    if (user.status === 1) statusArray = ["активный"];
                    else if (user.status === 2) statusArray = ["заблокирован"];

                    const meta = toOnlineMeta(user.online);

                    return {
                        userId: user.userId,
                        key: user.userId.toString(),
                        name: user.name,
                        internalId: user.internalId,
                        balance: user.balance,
                        pyataki: user.pyataki || 0,
                        online: meta.text,
                        status: statusArray,
                        statusValue: user.status,
                        onlineDays: meta.days,
                        onlineHours: meta.hours,
                        onlineTs: meta.ts,
                    };
                });

                setTableData(transformedData);
                setPagination((p) => ({ ...p, current: 1, pageSize: 10 }));
            }

            if (!usersResponse.success) {
                notification.error({
                    message: usersResponse.error.message || "Ошибка",
                    description: (usersResponse.error.description || []).join("\n"),
                });
            }
        };

        const requestControl = async () => {
            try {
                dispatch(setLoading(true));
                await getProfile();
                await getUsers();
            } finally {
                dispatch(setLoading(false));
            }
        };

        if (token) requestControl();
    }, [token, dispatch]);

    const normalized = useMemo(() => {
        const list = Array.isArray(tableData) ? tableData : [];
        return list.map((u) => ({
            ...u,
            userId: safeNum(u.userId),
            balance: safeNum(u.balance),
            statusValue: safeNum(u.statusValue),
            name: String(u.name ?? ""),
            online: String(u.online ?? "—"),
            onlineDays: u.onlineDays === null || u.onlineDays === undefined ? null : safeNum(u.onlineDays),
            onlineHours: u.onlineHours === null || u.onlineHours === undefined ? null : safeNum(u.onlineHours),
            onlineTs: u.onlineTs === null || u.onlineTs === undefined ? null : safeNum(u.onlineTs),
        }));
    }, [tableData]);

    const totals = useMemo(() => {
        const total = normalized.length;
        const active = normalized.filter((u) => u.statusValue === 1).length;
        const blocked = normalized.filter((u) => u.statusValue === 2).length;

        const totalBalance = normalized.reduce((sum, u) => sum + safeNum(u.balance), 0);
        const avgBalance = total ? Math.round(totalBalance / total) : 0;

        const online24h = normalized.filter((u) => Number.isFinite(u.onlineHours) && u.onlineHours !== null && u.onlineHours < 24).length;
        const online7d = normalized.filter((u) => Number.isFinite(u.onlineDays) && u.onlineDays !== null && u.onlineDays < 7).length;
        const offline30d = normalized.filter((u) => Number.isFinite(u.onlineDays) && u.onlineDays !== null && u.onlineDays >= 30).length;

        const activePct = total ? Math.round((active / total) * 100) : 0;
        const blockedPct = total ? Math.round((blocked / total) * 100) : 0;

        const sortedByBalance = [...normalized].sort((a, b) => safeNum(b.balance) - safeNum(a.balance));
        const topBalance = sortedByBalance.slice(0, 5);

        const risk = (() => {
            const b = blockedPct;
            const stale = total ? Math.round((offline30d / total) * 100) : 0;
            const score = Math.max(0, 100 - Math.max(b, stale));
            return { score, stale };
        })();

        return {
            total,
            active,
            blocked,
            totalBalance,
            avgBalance,
            online24h,
            online7d,
            offline30d,
            activePct,
            blockedPct,
            topBalance,
            risk,
        };
    }, [normalized]);

    const filtered = useMemo(() => {
        const q = String(query || "").trim().toLowerCase();
        const byStatus =
            statusFilter === "all"
                ? normalized
                : statusFilter === "active"
                    ? normalized.filter((u) => u.statusValue === 1)
                    : normalized.filter((u) => u.statusValue === 2);

        const byQuery = !q
            ? byStatus
            : byStatus.filter((u) => {
                const id = String(u.userId);
                const name = String(u.name || "").toLowerCase();
                const internalId = String(u.internalId || "").toLowerCase();
                return id.includes(q) || name.includes(q) || internalId.includes(q);
            });

        return byQuery;
    }, [normalized, statusFilter, query]);

    const filteredPaginationTotal = filtered.length;

    const editInitialValues = editSelected?.rawUser
        ? Object.keys(editSelected.rawUser).reduce((acc, k) => {
            acc[k] = editSelected.rawUser[k];
            return acc;
        }, {})
        : {};

    if (!token) {
        return <Redirect to={routeAuth()} />;
    }

    return (
        <>
            {!loading && (
                <>
                    <div className="container telegram-page">
                        <UserHeader />

                        <Row gutter={[16, 16]} align="middle">
                            <Col xs={24} md={14}>
                                <Space direction="vertical" size={4}>
                                    <Title level={3} style={{ marginBottom: 0 }}>
                                        Пользователи Telegram
                                    </Title>
                                    <Text type="secondary">Статистика, быстрые фильтры и управление профилями.</Text>
                                </Space>
                            </Col>
                            <Col xs={24} md={10} style={{ textAlign: "right" }}>
                                <Space wrap>
                                    <Segmented
                                        value={statusFilter}
                                        onChange={(v) => {
                                            setStatusFilter(v);
                                            setPagination((p) => ({ ...p, current: 1 }));
                                        }}
                                        options={[
                                            { label: "Все", value: "all", icon: <TeamOutlined /> },
                                            { label: "Активные", value: "active", icon: <CheckCircleOutlined /> },
                                            { label: "Блок", value: "blocked", icon: <CloseCircleOutlined /> },
                                        ]}
                                    />
                                    <Input
                                        allowClear
                                        value={query}
                                        onChange={(e) => {
                                            setQuery(e.target.value);
                                            setPagination((p) => ({ ...p, current: 1 }));
                                        }}
                                        prefix={<SearchOutlined />}
                                        placeholder="Поиск по User ID, Internal ID или имени"
                                        style={{ width: 260 }}
                                    />
                                    <Tooltip title={showAllBalances ? "Скрыть все балансы" : "Показать все балансы"}>
                                        <Button
                                            icon={showAllBalances ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                            onClick={() => {
                                                const next = !showAllBalances;
                                                showAllBalancesRef.current = next;
                                                setShowAllBalances(next);
                                            }}
                                            type={showAllBalances ? "primary" : "default"}
                                        >
                                            {showAllBalances ? "Балансы видны" : "Балансы скрыты"}
                                        </Button>
                                    </Tooltip>
                                    <Button icon={<BarChartOutlined />} onClick={() => setInsightsOpen(true)}>
                                        Инсайты
                                    </Button>
                                    <Button icon={<ReloadOutlined />} onClick={() => window.location.reload()}>
                                        Обновить
                                    </Button>
                                </Space>
                            </Col>
                        </Row>

                        <Divider />

                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={6}>
                                <Card bordered={false}>
                                    <Statistic title="Всего пользователей" value={totals.total} prefix={<TeamOutlined />} />
                                    <Text type="secondary">Записей в Telegram-профилях</Text>
                                </Card>
                            </Col>

                            <Col xs={24} md={6}>
                                <Card bordered={false}>
                                    <Statistic title="Активных" value={totals.active} prefix={<CheckCircleOutlined />} />
                                    <Progress percent={totals.activePct} showInfo={false} strokeColor="#52c41a" style={{ marginTop: 10 }} />
                                </Card>
                            </Col>

                            <Col xs={24} md={6}>
                                <Card bordered={false}>
                                    <Statistic title="Заблокированных" value={totals.blocked} prefix={<CloseCircleOutlined />} />
                                    <Progress percent={totals.blockedPct} showInfo={false} strokeColor="#fa541c" style={{ marginTop: 10 }} />
                                </Card>
                            </Col>

                            <Col xs={24} md={6}>
                                <Card bordered={false}>
                                    <Statistic
                                        title="Суммарный баланс"
                                        value={totals.totalBalance}
                                        prefix={<WalletOutlined />}
                                        formatter={(v) => Number(v).toLocaleString("ru-RU")}
                                    />
                                    <Text type="secondary">Средний: {totals.avgBalance.toLocaleString("ru-RU")}</Text>
                                </Card>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                            <Col xs={24} md={8}>
                                <Collapse
                                    bordered={false}
                                    defaultActiveKey={[]}
                                    items={[
                                        {
                                            key: "online",
                                            label: (
                                                <Space>
                                                    <ClockCircleOutlined />
                                                    <span>Активность онлайн</span>
                                                </Space>
                                            ),
                                            children: (
                                                <>
                                                    <Row gutter={[12, 12]}>
                                                        <Col span={12}>
                                                            <Card bordered={false}>
                                                                <Statistic title="За 24 часа" value={totals.online24h} />
                                                            </Card>
                                                        </Col>
                                                        <Col span={12}>
                                                            <Card bordered={false}>
                                                                <Statistic title="За 7 дней" value={totals.online7d} />
                                                            </Card>
                                                        </Col>
                                                    </Row>
                                                    <Divider style={{ margin: "12px 0" }} />
                                                    <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                                        <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                                            <Text type="secondary">Давно не заходили (≥30д)</Text>
                                                            <Text strong>{totals.offline30d}</Text>
                                                        </Space>
                                                        <Progress
                                                            percent={totals.total ? Math.round((totals.offline30d / totals.total) * 100) : 0}
                                                            showInfo={false}
                                                            strokeColor="#faad14"
                                                        />
                                                    </Space>
                                                </>
                                            ),
                                        },
                                    ]}
                                />
                            </Col>

                            <Col xs={24} md={8}>
                                <Collapse
                                    bordered={false}
                                    defaultActiveKey={[]}
                                    items={[
                                        {
                                            key: "balance",
                                            label: (
                                                <Space>
                                                    <FundOutlined />
                                                    <span>Топ по балансу</span>
                                                </Space>
                                            ),
                                            children: totals.topBalance.length ? (
                                                <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                                    {totals.topBalance.map((u) => {
                                                        const pct = totals.topBalance[0]?.balance ? Math.round((safeNum(u.balance) / safeNum(totals.topBalance[0].balance)) * 100) : 0;
                                                        return (
                                                            <Card key={u.userId} bordered={false}>
                                                                <Space direction="vertical" size={6} style={{ width: "100%" }}>
                                                                    <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                                                        <Space size={10} align="center">
                                                                            <Text strong>#{u.userId}</Text>
                                                                            <Tag color={u.statusValue === 2 ? "volcano" : "green"}>{u.statusValue === 2 ? "BLOCK" : "ACTIVE"}</Tag>
                                                                        </Space>
                                                                        <Text>{safeNum(u.balance).toLocaleString("ru-RU")}</Text>
                                                                    </Space>
                                                                    <Text type="secondary">{toShort(u.name, 44)}</Text>
                                                                    <Progress percent={pct} showInfo={false} strokeColor="#1677ff" />
                                                                </Space>
                                                            </Card>
                                                        );
                                                    })}
                                                </Space>
                                            ) : (
                                                <Empty description="Нет данных" />
                                            ),
                                        },
                                    ]}
                                />
                            </Col>

                            <Col xs={24} md={8}>
                                <Collapse
                                    bordered={false}
                                    defaultActiveKey={[]}
                                    items={[
                                        {
                                            key: "health",
                                            label: (
                                                <Space>
                                                    <DatabaseOutlined />
                                                    <span>Индекс здоровья базы</span>
                                                </Space>
                                            ),
                                            children: (
                                                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                                                    <Card bordered={false}>
                                                        <Space direction="vertical" size={6} style={{ width: "100%" }}>
                                                            <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                                                <Text type="secondary">Score</Text>
                                                                <Text strong>{totals.risk.score}/100</Text>
                                                            </Space>
                                                            <Progress
                                                                percent={totals.risk.score}
                                                                strokeColor={totals.risk.score >= 75 ? "#52c41a" : totals.risk.score >= 50 ? "#faad14" : "#ff4d4f"}
                                                            />
                                                        </Space>
                                                    </Card>

                                                    <Alert
                                                        type={totals.risk.score >= 75 ? "success" : totals.risk.score >= 50 ? "warning" : "error"}
                                                        showIcon
                                                        message={totals.risk.score >= 75 ? "Всё выглядит стабильно" : totals.risk.score >= 50 ? "Есть зоны внимания" : "Нужно вмешательство"}
                                                        description={
                                                            <Space direction="vertical" size={4}>
                                                                <Text type="secondary">
                                                                    Доля блокировок: <Text strong>{totals.blockedPct}%</Text>
                                                                </Text>
                                                                <Text type="secondary">
                                                                    Доля "спящих" (≥30д): <Text strong>{totals.risk.stale}%</Text>
                                                                </Text>
                                                            </Space>
                                                        }
                                                    />
                                                </Space>
                                            ),
                                        },
                                    ]}
                                />
                            </Col>
                        </Row>

                        <Divider />

                        <Card
                            bordered={false}
                            title="Таблица пользователей"
                            extra={
                                <Space>
                                    <Tooltip title="Показано с учётом фильтров и поиска">
                                        <Tag color="blue">{filteredPaginationTotal} записей</Tag>
                                    </Tooltip>
                                    <Button onClick={() => setInsightsOpen(true)} icon={<BarChartOutlined />}>
                                        Подробнее
                                    </Button>
                                </Space>
                            }
                        >
                            <Table
                                columns={columns}
                                dataSource={filtered}
                                rowKey="userId"
                                onChange={(nextPagination) => {
                                    setPagination({
                                        current: nextPagination.current,
                                        pageSize: nextPagination.pageSize,
                                    });
                                }}
                                pagination={{
                                    current: pagination.current,
                                    pageSize: 10,
                                    total: filteredPaginationTotal,
                                    showSizeChanger: false,
                                }}
                            />
                        </Card>
                    </div>

                    <Drawer
                        open={insightsOpen}
                        onClose={() => setInsightsOpen(false)}
                        title="Инсайты по базе пользователей"
                        width={640}
                        destroyOnClose
                        extra={
                            <Button onClick={() => setInsightsOpen(false)}>
                                Закрыть
                            </Button>
                        }
                    >
                        <Space direction="vertical" size={14} style={{ width: "100%" }}>
                            <Row gutter={[12, 12]}>
                                <Col span={12}>
                                    <Card bordered={false}>
                                        <Statistic title="Всего" value={totals.total} prefix={<TeamOutlined />} />
                                    </Card>
                                </Col>
                                <Col span={12}>
                                    <Card bordered={false}>
                                        <Statistic title="Средний баланс" value={totals.avgBalance} prefix={<WalletOutlined />} />
                                    </Card>
                                </Col>
                            </Row>

                            <Card bordered={false} title="Состав по статусам">
                                <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                    <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                        <Text type="secondary">Активные</Text>
                                        <Text strong>
                                            {totals.active} ({totals.activePct}%)
                                        </Text>
                                    </Space>
                                    <Progress percent={totals.activePct} showInfo={false} strokeColor="#52c41a" />
                                    <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                        <Text type="secondary">Заблокированные</Text>
                                        <Text strong>
                                            {totals.blocked} ({totals.blockedPct}%)
                                        </Text>
                                    </Space>
                                    <Progress percent={totals.blockedPct} showInfo={false} strokeColor="#fa541c" />
                                </Space>
                            </Card>

                            <Card bordered={false} title="Активность">
                                <Descriptions
                                    column={1}
                                    items={[
                                        { key: "online24h", label: "Онлайн за 24 часа", children: totals.online24h },
                                        { key: "online7d", label: "Онлайн за 7 дней", children: totals.online7d },
                                        { key: "offline30d", label: "Не заходили ≥30 дней", children: totals.offline30d },
                                    ]}
                                />
                            </Card>

                            <Card bordered={false} title="Топ-5 по балансу">
                                <Table
                                    size="small"
                                    pagination={false}
                                    rowKey="userId"
                                    dataSource={totals.topBalance}
                                    columns={[
                                        {
                                            title: "User ID",
                                            dataIndex: "userId",
                                            key: "userId",
                                            width: 110,
                                            render: (v) => <Text strong>#{v}</Text>,
                                        },
                                        {
                                            title: "Имя",
                                            dataIndex: "name",
                                            key: "name",
                                            ellipsis: true,
                                            render: (v) => <Text>{v}</Text>,
                                        },
                                        {
                                            title: "Статус",
                                            dataIndex: "statusValue",
                                            key: "statusValue",
                                            width: 120,
                                            render: (v) => <Tag color={safeNum(v) === 2 ? "volcano" : "green"}>{safeNum(v) === 2 ? "BLOCK" : "ACTIVE"}</Tag>,
                                        },
                                        {
                                            title: "Баланс",
                                            dataIndex: "balance",
                                            key: "balance",
                                            width: 140,
                                            render: (v) => <Text>{safeNum(v).toLocaleString("ru-RU")}</Text>,
                                        },
                                        {
                                            title: "",
                                            key: "actions",
                                            width: 60,
                                            render: (_, r) => (
                                                <Tooltip title="Редактировать">
                                                    <Button type="text" icon={<EditOutlined />} onClick={() => openEditModal(r.userId)} />
                                                </Tooltip>
                                            ),
                                        },
                                    ]}
                                />
                            </Card>

                            <Alert
                                showIcon
                                type="info"
                                message="Подсказка"
                                description="Фильтры и поиск наверху страницы влияют на таблицу. Действия (баланс/редактирование/удаление) доступны прямо из таблицы."
                            />
                        </Space>
                    </Drawer>

                    {selectedUser && (
                        <Modal
                            key={`balance-${selectedUser.userId}-${balanceModalKey}`}
                            open={balanceModalOpen}
                            title={null}
                            onCancel={closeBalanceModal}
                            footer={null}
                            width={520}
                            styles={{ body: { padding: "4px 0 0" } }}
                        >
                            <div style={{ textAlign: "center", marginBottom: 20 }}>
                                <WalletOutlined style={{ fontSize: 28, color: "#1677ff", marginBottom: 8 }} />
                                <div style={{ fontSize: 16, fontWeight: 600 }}>{selectedUser.name}</div>
                                <Text type="secondary" style={{ fontSize: 13 }}>ID: {selectedUser.userId}</Text>
                            </div>

                            <Row gutter={12} style={{ marginBottom: 20 }}>
                                <Col span={8}>
                                    <Card size="small" style={{ background: "#fffbe6", borderColor: "#ffe58f", textAlign: "center" }}>
                                        <Statistic
                                            title={<span style={{ fontSize: 12 }}>Пятаки</span>}
                                            value={selectedUser.pyataki}
                                            suffix="пят."
                                            valueStyle={{ color: "#d48806", fontSize: 22 }}
                                        />
                                    </Card>
                                </Col>
                                <Col span={8}>
                                    <Card size="small" style={{ background: "#f6ffed", borderColor: "#b7eb8f", textAlign: "center" }}>
                                        <Statistic
                                            title={<span style={{ fontSize: 12 }}>BRU</span>}
                                            value={selectedUser.balance}
                                            precision={2}
                                            suffix="BRU"
                                            valueStyle={{ color: "#389e0d", fontSize: 22 }}
                                        />
                                    </Card>
                                </Col>
                                <Col span={8}>
                                    <Card size="small" style={{ background: "#f0f5ff", borderColor: "#adc6ff", textAlign: "center" }}>
                                        <Statistic
                                            title={<span style={{ fontSize: 12 }}>Игр сыграно</span>}
                                            value={selectedUser.gamesPlayed}
                                            valueStyle={{ color: "#1677ff", fontSize: 22 }}
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            <Divider style={{ margin: "0 0 16px" }} />

                            <div style={{ marginBottom: 20 }}>
                                <Text strong style={{ fontSize: 13, display: "block", marginBottom: 8, color: "#d48806" }}>
                                    <DollarOutlined /> Пятаки (игровая валюта)
                                </Text>
                                <Space.Compact style={{ width: "100%" }}>
                                    <InputNumber
                                        style={{ flex: 1 }}
                                        placeholder="Количество"
                                        min={0}
                                        value={balanceInputs.pyataki === "" ? null : balanceInputs.pyataki}
                                        onChange={(v) => setBalanceInputs((p) => ({ ...p, pyataki: v ?? "" }))}
                                    />
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        loading={balanceConfirmLoading}
                                        disabled={!balanceInputs.pyataki}
                                        onClick={() => submitBalanceAction("pyataki", "add")}
                                    >
                                        Добавить
                                    </Button>
                                    <Button
                                        danger
                                        icon={<MinusOutlined />}
                                        loading={balanceConfirmLoading}
                                        disabled={!balanceInputs.pyataki}
                                        onClick={() => submitBalanceAction("pyataki", "subtract")}
                                    >
                                        Списать
                                    </Button>
                                </Space.Compact>
                            </div>

                            {canManageBru && (
                                <div>
                                    <Text strong style={{ fontSize: 13, display: "block", marginBottom: 8, color: "#389e0d" }}>
                                        <DollarOutlined /> BRU (белорусские рубли)
                                    </Text>
                                    <Space.Compact style={{ width: "100%" }}>
                                        <InputNumber
                                            style={{ flex: 1 }}
                                            placeholder="Сумма"
                                            min={0}
                                            step={0.01}
                                            value={balanceInputs.balance === "" ? null : balanceInputs.balance}
                                            onChange={(v) => setBalanceInputs((p) => ({ ...p, balance: v ?? "" }))}
                                        />
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            loading={balanceConfirmLoading}
                                            disabled={!balanceInputs.balance}
                                            onClick={() => submitBalanceAction("balance", "add")}
                                        >
                                            Добавить
                                        </Button>
                                        <Button
                                            danger
                                            icon={<MinusOutlined />}
                                            loading={balanceConfirmLoading}
                                            disabled={!balanceInputs.balance}
                                            onClick={() => submitBalanceAction("balance", "subtract")}
                                        >
                                            Списать
                                        </Button>
                                    </Space.Compact>
                                </div>
                            )}
                        </Modal>
                    )}

                    <EditModal
                        open={editModalOpen}
                        title={editSelected ? `Редактирование: ${editSelected.displayName} (ID: ${editSelected.userId})` : "Редактирование"}
                        onClose={closeEditModal}
                        onSubmit={submitEditUser}
                        confirmLoading={editConfirmLoading}
                        initialValues={editInitialValues}
                        okText="Сохранить"
                        cancelText="Отмена"
                        fields={buildEditFields(editSelected?.rawUser)}
                    />

                    <Modal
                        open={logsModalOpen}
                        title={
                            logsModalUser
                                ? `Логи пользователя: ${logsModalUser.name} (ID: ${logsModalUser.userId})`
                                : "Логи пользователя"
                        }
                        onCancel={closeLogsModal}
                        footer={[
                            <Button key="close" onClick={closeLogsModal}>
                                Закрыть
                            </Button>,
                        ]}
                        width={900}
                        destroyOnClose
                    >
                        {logsModalLoading ? (
                            <div style={{ textAlign: "center", padding: 40 }}>
                                <Spin size="large" />
                            </div>
                        ) : userLogs.length === 0 ? (
                            <Empty description="Нет логов для этого пользователя" />
                        ) : (
                            <>
                                <Alert
                                    type="info"
                                    showIcon
                                    message="Кликните на строку, чтобы посмотреть детали"
                                    style={{ marginBottom: 16 }}
                                />
                                <Table
                                    size="small"
                                    rowKey="operationId"
                                    dataSource={userLogs}
                                    onRow={(record) => ({
                                        onClick: () => {
                                            setSelectedLog(record);
                                            setLogDetailOpen(true);
                                        },
                                        style: { cursor: "pointer" },
                                    })}
                                    pagination={{
                                        pageSize: 10,
                                        showSizeChanger: true,
                                        pageSizeOptions: ["10", "20", "50"],
                                        showTotal: (t) => `Всего: ${t}`,
                                    }}
                                    columns={[
                                        {
                                            title: "ID",
                                            dataIndex: "operationId",
                                            key: "operationId",
                                            width: 70,
                                            render: (v) => <Text strong>#{v}</Text>,
                                        },
                                        {
                                            title: "Действие",
                                            dataIndex: "action",
                                            key: "action",
                                            width: 200,
                                            render: (v) => (
                                                <Tooltip title={v}>
                                                    <Tag color="blue">{getActionLabel(v)}</Tag>
                                                </Tooltip>
                                            ),
                                        },
                                        {
                                            title: "Заголовок",
                                            dataIndex: "title",
                                            key: "title",
                                            ellipsis: true,
                                            render: (v) => <Text>{v || "—"}</Text>,
                                        },
                                        {
                                            title: "Дата",
                                            dataIndex: "createdAt",
                                            key: "createdAt",
                                            width: 160,
                                            sorter: (a, b) => {
                                                const da = dayjs(a.createdAt);
                                                const db = dayjs(b.createdAt);
                                                return da.valueOf() - db.valueOf();
                                            },
                                            defaultSortOrder: "descend",
                                            render: (v) => {
                                                const d = dayjs(v);
                                                return d.isValid() ? (
                                                    <Text type="secondary">{d.format("DD.MM.YYYY HH:mm")}</Text>
                                                ) : (
                                                    "—"
                                                );
                                            },
                                        },
                                    ]}
                                />
                            </>
                        )}
                    </Modal>

                    <Modal
                        open={logDetailOpen}
                        title={selectedLog ? `Лог #${selectedLog.operationId}` : "Детали лога"}
                        onCancel={() => {
                            setLogDetailOpen(false);
                            setSelectedLog(null);
                        }}
                        footer={[
                            <Button
                                key="close"
                                onClick={() => {
                                    setLogDetailOpen(false);
                                    setSelectedLog(null);
                                }}
                            >
                                Закрыть
                            </Button>,
                        ]}
                        width={700}
                        destroyOnClose
                    >
                        {selectedLog && (
                            <Descriptions bordered column={1} size="small">
                                <Descriptions.Item label="ID операции">
                                    <Text strong>#{selectedLog.operationId}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Действие">
                                    <Space>
                                        <Tag color="blue">{getActionLabel(selectedLog.action)}</Tag>
                                        <Text type="secondary">({selectedLog.action})</Text>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="Заголовок">
                                    <Text>{selectedLog.title || "—"}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Дата создания">
                                    <Text>
                                        {dayjs(selectedLog.createdAt).isValid()
                                            ? dayjs(selectedLog.createdAt).format("DD.MM.YYYY HH:mm:ss")
                                            : "—"}
                                    </Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Admin User ID">
                                    <Text>{selectedLog.adminUserId || "—"}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Детали (meta)">
                                    {selectedLog.meta && Object.keys(selectedLog.meta).length > 0 ? (
                                        <pre style={{ margin: 0, whiteSpace: "pre-wrap", background: "#f5f5f5", padding: 12, borderRadius: 6 }}>
                                            {JSON.stringify(selectedLog.meta, null, 2)}
                                        </pre>
                                    ) : (
                                        <Text type="secondary">—</Text>
                                    )}
                                </Descriptions.Item>
                            </Descriptions>
                        )}
                    </Modal>

                    <Modal
                        open={historyModalOpen}
                        onCancel={closeHistoryModal}
                        title={historyModalUser ? `История операций: ${historyModalUser.name} (ID: ${historyModalUser.userId})` : "История операций"}
                        footer={[
                            <Button key="close" onClick={closeHistoryModal}>
                                Закрыть
                            </Button>,
                        ]}
                        width={900}
                        destroyOnClose
                    >
                        {historyModalLoading ? (
                            <div style={{ textAlign: 'center', padding: 40 }}>
                                <Spin size="large" />
                            </div>
                        ) : userHistory.length === 0 ? (
                            <Empty description="Нет операций" />
                        ) : (
                            <Table
                                size="small"
                                dataSource={userHistory}
                                rowKey={(r) => r._id || Math.random()}
                                pagination={{ pageSize: 10 }}
                                columns={[
                                    {
                                        title: "Дата",
                                        dataIndex: "createdAt",
                                        key: "createdAt",
                                        width: 150,
                                        render: (v) => dayjs(v).format("DD.MM.YYYY HH:mm"),
                                    },
                                    {
                                        title: "Действие",
                                        dataIndex: "action",
                                        key: "action",
                                        width: 130,
                                        render: (v) => <Tag color="blue">{v}</Tag>,
                                    },
                                    {
                                        title: "Тип",
                                        dataIndex: "type",
                                        key: "type",
                                        width: 100,
                                        render: (v) => <Tag color={v === 'income' ? 'green' : 'red'}>{v === 'income' ? 'Приход' : 'Расход'}</Tag>,
                                    },
                                    {
                                        title: "Сумма",
                                        dataIndex: "amount",
                                        key: "amount",
                                        width: 100,
                                        render: (v, r) => {
                                            const cur = (r.currency || '').toLowerCase();
                                            const label = cur === 'byn' || cur === 'bru' ? 'BRU' : cur === 'pyataki' ? 'пят.' : r.currency;
                                            return `${v} ${label}`;
                                        },
                                    },
                                    {
                                        title: "Описание",
                                        dataIndex: "description",
                                        key: "description",
                                        ellipsis: true,
                                    },
                                ]}
                            />
                        )}
                    </Modal>

                    <Modal
                        open={referralModalOpen}
                        onCancel={closeReferralModal}
                        title={referralModalUser ? `Реферальная статистика: ${referralModalUser.name} (ID: ${referralModalUser.userId})` : "Рефералы"}
                        footer={[
                            <Button key="close" onClick={closeReferralModal}>
                                Закрыть
                            </Button>,
                        ]}
                        width={800}
                        destroyOnClose
                    >
                        {referralModalLoading ? (
                            <div style={{ textAlign: 'center', padding: 40 }}>
                                <Spin size="large" />
                            </div>
                        ) : !referralData ? (
                            <Empty description="Нет реферальных данных" />
                        ) : (
                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                {referralData.referralCode && (
                                    <Card bordered={false} size="small">
                                        <Descriptions column={2} size="small">
                                            <Descriptions.Item label="Реферальный код">
                                                <Text strong copyable>{referralData.referralCode}</Text>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Ссылка">
                                                <Text copyable ellipsis style={{ maxWidth: 300 }}>{referralData.link}</Text>
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </Card>
                                )}

                                <Row gutter={[16, 16]}>
                                    <Col span={8}>
                                        <Card bordered={false}>
                                            <Statistic title="Всего приглашено" value={referralData.totalInvited || 0} />
                                        </Card>
                                    </Col>
                                    <Col span={8}>
                                        <Card bordered={false}>
                                            <Statistic title="Сыграли" value={referralData.totalPlayed || 0} valueStyle={{ color: '#52c41a' }} />
                                        </Card>
                                    </Col>
                                    <Col span={8}>
                                        <Card bordered={false}>
                                            <Statistic title="Ожидают" value={referralData.totalPending || 0} valueStyle={{ color: '#faad14' }} />
                                        </Card>
                                    </Col>
                                </Row>

                                <Row gutter={[16, 16]}>
                                    <Col span={12}>
                                        <Card bordered={false}>
                                            <Statistic title="Начислено бонусов" value={referralData.awardedBonus || 0} suffix="пятаков" valueStyle={{ color: '#1677ff' }} />
                                        </Card>
                                    </Col>
                                    <Col span={12}>
                                        <Card bordered={false}>
                                            <Statistic title="Ожидается" value={referralData.pendingBonus || 0} suffix="пятаков" valueStyle={{ color: '#999' }} />
                                        </Card>
                                    </Col>
                                </Row>

                                {referralData.invitedUsers && referralData.invitedUsers.length > 0 && (
                                    <Card bordered={false} title="Приглашённые пользователи">
                                        <Table
                                            size="small"
                                            dataSource={referralData.invitedUsers}
                                            rowKey={(r) => r.userId}
                                            pagination={{ pageSize: 5 }}
                                            columns={[
                                                {
                                                    title: "ID",
                                                    dataIndex: "userId",
                                                    key: "userId",
                                                    width: 80,
                                                },
                                                {
                                                    title: "Имя",
                                                    dataIndex: "name",
                                                    key: "name",
                                                    ellipsis: true,
                                                },
                                                {
                                                    title: "Дата регистрации",
                                                    dataIndex: "registeredAt",
                                                    key: "registeredAt",
                                                    width: 140,
                                                    render: (v) => dayjs(v).format("DD.MM.YYYY"),
                                                },
                                                {
                                                    title: "Статус",
                                                    dataIndex: "status",
                                                    key: "status",
                                                    width: 120,
                                                    render: (v) => (
                                                        <Tag color={v === 'completed' ? 'green' : v === 'played' ? 'blue' : 'default'}>
                                                            {v === 'completed' ? 'Завершено' : v === 'played' ? 'Сыграл' : 'Ожидает'}
                                                        </Tag>
                                                    ),
                                                },
                                                {
                                                    title: "Бонус",
                                                    dataIndex: "bonusAwarded",
                                                    key: "bonusAwarded",
                                                    width: 100,
                                                    render: (v) => v > 0 ? `+${v}` : '—',
                                                },
                                            ]}
                                        />
                                    </Card>
                                )}
                            </Space>
                        )}
                    </Modal>
                </>
            )}
        </>
    );
};

export { routeAdmin };

export default AdminPage;